import React, { useRef, useEffect } from 'react';

const ExamMiniNav = ({ inline = false, isReviewMode, mode, questions, answers, currentQuestionIndex, setCurrentQuestionIndex, onDemandAnswers, isHistoryVuReview = false }) => {

    const miniNavRef = useRef(null);

    // Auto-scroll to keep the current question button visible and centered
    useEffect(() => {
        const container = miniNavRef.current;
        if (!container) return;
        const btn = container.children[currentQuestionIndex];
        if (!btn) return;
        const containerCenter = container.scrollLeft + container.clientWidth / 2;
        const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
        const diff = btnCenter - containerCenter;
        if (Math.abs(diff) > btn.offsetWidth) {
            container.scrollBy({ left: diff, behavior: 'smooth' });
        }
    }, [currentQuestionIndex]);

    const scrollMiniNav = (direction) => {
        if (miniNavRef.current) {
            const scrollAmount = miniNavRef.current.clientWidth;
            miniNavRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // inline = rendered inside ExamHeader on desktop (no wrapper, single row)
    const outerClass = inline
        ? 'w-full'
        : 'w-full bg-white/80 dark:bg-black/40 border-b border-gray-200 dark:border-white/5 backdrop-blur-md sticky top-0 z-40 transition-colors duration-500 py-2';

    const innerClass = inline
        ? 'flex items-center gap-2'
        : 'max-w-3xl mx-auto px-2 flex items-center gap-2';

    const gridClass = inline
        ? 'flex-1 flex gap-1 overflow-x-auto no-scrollbar scroll-smooth px-1'
        : 'flex-1 flex gap-1 overflow-x-auto no-scrollbar scroll-smooth px-1';

    const questionButtons = questions.map((q, idx) => {
        const isCurrent = currentQuestionIndex === idx;
        const answerValue = answers[q.id];
        // A question is "answered" if it has a real answer (not 0, not undefined)
        const isAnswered = answerValue !== undefined && answerValue !== 0 && answerValue !== '0';
        // A question is "seen but not answered" if value is 0
        const isSeenNotAnswered = answerValue === 0 || answerValue === '0';

        // Flash mode: can go back to answered questions, can't jump ahead to unanswered
        const isLocked = mode === 'flash' && !isReviewMode && (
            (idx > currentQuestionIndex && !isAnswered) || (idx < currentQuestionIndex && !isAnswered)
        );

        // New question in vu-examen history review (answer undefined = not in original exam)
        const isNewVuQuestion = isHistoryVuReview && isReviewMode && answerValue === undefined;

        let baseClass = 'bg-gray-400/80 dark:bg-white/30 border-transparent text-gray-900 dark:text-white hover:bg-gray-500/80 dark:hover:bg-white/40';

        let isCorrect = false;
        let isWrong = false;
        let isStaleAnswer = false;
        // Show correct/wrong in review mode AND flash mode (when onDemandAnswers available)
        if (isReviewMode || mode === 'flash') {
            const secret = onDemandAnswers[q.id];
            const userAns = answers[q.id];
            if (userAns !== undefined && userAns !== 0 && userAns !== '0' && secret) {
                // Check if user's historical answer still exists in current options
                const answerExistsInOptions = q.options?.some(opt => String(opt.id) === String(userAns));
                if (!answerExistsInOptions && isReviewMode) {
                    // Options were modified since exam was taken — can't compare
                    isStaleAnswer = true;
                } else if (String(userAns) === String(secret.correctReponseId)) {
                    isCorrect = true;
                } else {
                    isWrong = true;
                }
            }
        }

        if (isLocked) {
            baseClass = 'bg-gray-200 dark:bg-white/[0.05] border-transparent text-gray-600 dark:text-gray-400 cursor-not-allowed opacity-80';
        } else if (isCurrent && isNewVuQuestion) {
            // Current new vu-examen question — amber highlight
            baseClass = 'bg-amber-500 text-white shadow-md ring-2 ring-amber-400 scale-105';
        } else if (isCurrent && isStaleAnswer) {
            // Current question with obsolete answer — gray/neutral highlight
            baseClass = 'bg-gray-500 text-white shadow-md ring-2 ring-gray-400 scale-105';
        } else if (isCurrent) {
            if (isCorrect || isWrong) {
                baseClass = isCorrect
                    ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-400 scale-105'
                    : 'bg-red-600 text-white shadow-md ring-2 ring-red-400 scale-105';
            } else if (isSeenNotAnswered) {
                // Current question that was seen but not answered — orange highlight
                baseClass = 'bg-orange-500 text-white shadow-md ring-2 ring-orange-400 scale-105';
            } else {
                baseClass = 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md scale-105';
            }
        } else if (isNewVuQuestion) {
            // New vu-examen question (not current) — amber/dashed style
            baseClass = 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-500/30 border-dashed';
        } else if (isStaleAnswer) {
            // Obsolete answer (options changed since exam) — gray/neutral
            baseClass = 'bg-gray-200 dark:bg-white/10 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-white/20';
        } else if (isCorrect) {
            baseClass = 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30';
        } else if (isWrong) {
            baseClass = 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30';
        } else if (isSeenNotAnswered) {
            // Seen but not answered — orange indicator
            baseClass = 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-500/30';
        } else if (isAnswered) {
            baseClass = 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
        }

        return (
            <button
                key={idx}
                onClick={() => !isLocked && setCurrentQuestionIndex(idx)}
                disabled={isLocked}
                className={`w-7 h-7 rounded-lg text-xs font-bold transition-all border ${baseClass} flex items-center justify-center flex-shrink-0`}
            >
                {idx + 1}
            </button>
        );
    });

    return (
        <div className={outerClass}>
            <div className={innerClass}>
                <button
                    onClick={() => scrollMiniNav('left')}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 transition flex-shrink-0"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <div
                    ref={miniNavRef}
                    className={gridClass}
                >
                    {questionButtons}
                </div>

                <button
                    onClick={() => scrollMiniNav('right')}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 transition flex-shrink-0"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default ExamMiniNav;
